// Single place the browser gets its bearer token from.
//
// The backend's GET /api/auth/config says which sign-in the deployment uses:
//
//   local  - password form; the app's own JWT is kept in localStorage under
//            "token" (unchanged behaviour)
//   entra  - Microsoft Entra External ID via MSAL; the API access token is
//            held in memory here and refreshed silently. Nothing auth-related
//            is written to localStorage. MSAL keeps its own cache in
//            sessionStorage (cleared when the tab closes).
//   both   - either, whichever session exists
//
// Everything else in the app calls getStoredToken() (sync, for the many
// places that build an Authorization header themselves) or getAccessToken()
// (async, used by the fetch/axios interceptors in index.js, refreshes when
// the Entra token is about to expire).

import API from './apiBase';

const TOKEN_KEY = 'token';
const REFRESH_MARGIN_MS = 2 * 60 * 1000;
const FALLBACK_CONFIG = {
  auth_mode: 'local',
  environment: 'unknown',
  local_login_enabled: true,
  entra_enabled: false,
  entra: null,
  roles: [],
};

let config = null;
let msalInstance = null;
let msalModule = null;
let entraToken = null; // { accessToken, expiresOn }
let bootstrapPromise = null;

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export async function loadAuthConfig() {
  if (config) return config;
  try {
    const response = await fetch(`${API}/auth/config`);
    if (response.ok) {
      config = await response.json();
    }
  } catch (err) {
    console.warn('[auth] could not load /auth/config; assuming password sign-in', err);
  }
  if (!config) config = { ...FALLBACK_CONFIG };
  return config;
}

export function getAuthConfig() {
  return config || FALLBACK_CONFIG;
}

export function authMode() {
  return getAuthConfig().auth_mode || 'local';
}

export function isEntraEnabled() {
  return Boolean(getAuthConfig().entra_enabled && getAuthConfig().entra);
}

export function isLocalLoginEnabled() {
  return config ? Boolean(config.local_login_enabled) : true;
}

// ---------------------------------------------------------------------------
// MSAL (Entra)
// ---------------------------------------------------------------------------

function msalConfig(entra) {
  return {
    auth: {
      clientId: entra.spa_client_id,
      authority: entra.authority,
      knownAuthorities: entra.known_authorities || [],
      redirectUri: `${window.location.origin}/login`,
      postLogoutRedirectUri: `${window.location.origin}/login`,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'sessionStorage',
    },
  };
}

async function loadMsal() {
  if (!msalModule) {
    // Lazy: password-only deployments never download MSAL.
    msalModule = await import('@azure/msal-browser');
  }
  return msalModule;
}

function activeAccount() {
  return msalInstance ? msalInstance.getActiveAccount() : null;
}

function tokenIsFresh() {
  if (!entraToken || !entraToken.accessToken) return false;
  const expires = entraToken.expiresOn ? new Date(entraToken.expiresOn).getTime() : 0;
  return expires - Date.now() > REFRESH_MARGIN_MS;
}

async function acquireEntraToken({ interactive = false } = {}) {
  const account = activeAccount();
  if (!msalInstance || !account) return null;
  const { InteractionRequiredAuthError } = await loadMsal();
  const request = { scopes: [getAuthConfig().entra.api_scope], account };
  try {
    const result = await msalInstance.acquireTokenSilent(request);
    entraToken = { accessToken: result.accessToken, expiresOn: result.expiresOn };
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError && interactive) {
      await msalInstance.acquireTokenRedirect(request);
      return null;
    }
    throw err;
  }
}

/**
 * Load config and, for Entra deployments, initialise MSAL and finish any
 * redirect that is in flight. Call once before rendering (index.js). Safe to
 * await from anywhere afterwards; it resolves to the config.
 */
export async function bootstrapAuth() {
  if (!bootstrapPromise) {
    bootstrapPromise = (async () => {
      const cfg = await loadAuthConfig();
      if (cfg.entra_enabled && cfg.entra) {
        try {
          const { PublicClientApplication } = await loadMsal();
          msalInstance = new PublicClientApplication(msalConfig(cfg.entra));
          await msalInstance.initialize();
          const result = await msalInstance.handleRedirectPromise();
          if (result && result.account) {
            msalInstance.setActiveAccount(result.account);
          } else if (!msalInstance.getActiveAccount()) {
            const accounts = msalInstance.getAllAccounts();
            if (accounts.length > 0) msalInstance.setActiveAccount(accounts[0]);
          }
          if (msalInstance.getActiveAccount()) {
            try {
              await acquireEntraToken();
            } catch (err) {
              console.warn('[auth] silent token acquisition failed at bootstrap', err);
            }
          }
        } catch (err) {
          console.error('[auth] Microsoft sign-in could not be initialised', err);
          msalInstance = null;
        }
      }
      return cfg;
    })();
  }
  return bootstrapPromise;
}

// ---------------------------------------------------------------------------
// Token access
// ---------------------------------------------------------------------------

function readLocalToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (_) {
    return null;
  }
}

// Precedence: an explicit password session (localStorage token) always wins
// over a Microsoft account MSAL still remembers. Otherwise, in "both" mode, a
// rejected Microsoft identity would shadow a perfectly good password login on
// every page load. A password session only exists when the person typed a
// password, so it is the stronger statement of intent.

/** Synchronous: the current bearer token, or null when signed out. */
export function getStoredToken() {
  const local = readLocalToken();
  if (local) return local;
  if (isEntraEnabled() && activeAccount()) {
    return entraToken ? entraToken.accessToken : null;
  }
  return null;
}

/** Async: like getStoredToken() but refreshes an expiring Entra token first. */
export async function getAccessToken() {
  const local = readLocalToken();
  if (local) return local;
  if (isEntraEnabled() && activeAccount()) {
    if (tokenIsFresh()) return entraToken.accessToken;
    try {
      return await acquireEntraToken();
    } catch (err) {
      console.warn('[auth] token refresh failed', err);
      return entraToken ? entraToken.accessToken : null;
    }
  }
  return null;
}

/**
 * Forget the Microsoft account on this tab without a round trip to Microsoft.
 * Used when the backend refuses the identity (not invited), so the next
 * attempt starts clean and the password form is usable immediately.
 */
export async function dropEntraSession() {
  entraToken = null;
  if (!msalInstance) return;
  try {
    if (typeof msalInstance.clearCache === 'function') {
      await msalInstance.clearCache();
    }
    msalInstance.setActiveAccount(null);
  } catch (err) {
    console.warn('[auth] could not clear the Microsoft session cache', err);
  }
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith('msal.') || key.includes('login.windows.net') || key.includes('ciamlogin.com'))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch (_) {
    /* ignore */
  }
}

export function hasSession() {
  return Boolean(getStoredToken());
}

export function entraAccount() {
  return activeAccount();
}

// ---------------------------------------------------------------------------
// Sign in / out
// ---------------------------------------------------------------------------

export async function signInWithMicrosoft() {
  await bootstrapAuth();
  if (!msalInstance) {
    throw new Error('Microsoft sign-in is not configured on this deployment.');
  }
  await msalInstance.loginRedirect({ scopes: [getAuthConfig().entra.api_scope] });
}

/** Password flow: persist the app JWT and the (non-secret) profile. */
export function storeLocalSession(token, user) {
  localStorage.setItem(TOKEN_KEY, token);
  if (user) localStorage.setItem('user', JSON.stringify(user));
  // A deliberate password login supersedes any remembered Microsoft account.
  dropEntraSession();
}

export function storeUserProfile(user) {
  if (user) localStorage.setItem('user', JSON.stringify(user));
}

export function clearStoredToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (_) {
    /* ignore */
  }
  entraToken = null;
}

export function clearLocalProfile() {
  ['user', 'active_client_id', 'active_client_name'].forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (_) {
      /* ignore */
    }
  });
}

/** Full sign-out. For Entra sessions this ends the MSAL session too. */
export async function signOut({ redirect = '/login' } = {}) {
  clearStoredToken();
  clearLocalProfile();
  if (isEntraEnabled() && msalInstance) {
    const account = activeAccount();
    try {
      await msalInstance.logoutRedirect({
        account,
        postLogoutRedirectUri: `${window.location.origin}${redirect}`,
      });
      return;
    } catch (err) {
      console.warn('[auth] Microsoft sign-out failed; clearing local state only', err);
    }
  }
  window.location.href = redirect;
}

/** Test hook. */
export function __resetAuthSessionForTests() {
  config = null;
  msalInstance = null;
  msalModule = null;
  entraToken = null;
  bootstrapPromise = null;
}
